/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @author Alexander Golshtein
 * @version 1.24.0
 * @since 1.14.0
 */

import { Fragment, useCallback, useEffect } from 'react';

import { connect } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';
import { ILogBusinessEventPayload } from '@modules/logging/loggingTypes';

import Title from '../../../components/common/Title/Title';
import { EBasketType } from '../../../modules/app/appConstants';
import withAsyncStateManagement from '../../../modules/asyncStateManagment/WithAsyncStateManagement';
import PageLocale from '../../../modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '../../../modules/internationalization/constants';
import { makeSelectTranslationWizardChangeLock } from '../../../modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '../../../modules/internationalization/types/common/pages';
import { IWizardChangeLockTranslation } from '../../../modules/internationalization/types/wizards/changeLock';
import Wizard from '../../../modules/wizard/components/Wizard';
import { EWizardName, WIZARD_ALL_ENTITIES } from '../../../modules/wizard/wizardConstants';
import { IWizardState } from '../../../modules/wizard/wizardReducer';
import { makeSelectWizardStepName } from '../../../modules/wizard/wizardSelectors';
import { IRootReducer } from '../../../reducers/rootReducer';
import { IRouteParams } from '../../../routes/constants/routesConstants';
import { EChangeLockOperationType, IWizardChangeLockStartData } from '../types/wizardChangeLockTypes';
import { wizardChangeLockHandleNextAction } from '../wizardChangeLockActions';
import { EWizardChangeLockSteps } from '../wizardChangeLockConstant';
import wizardChangeLockReducer, { IWizardChangeLockReducer } from '../wizardChangeLockReducer';
import { makeSelectWizardChangeLockValidation } from '../wizardChangeLockSelectors';
import WizardChangeLockConfirmation from './WizardChangeLockConfirmation';
import WizardChangeLockDateSelection from './WizardChangeLockDateSelection';
import WizardChangeLockOperationSelection from './WizardChangeLockOperationSelection';

const WizardChangeLock = (props: IConnectedProps & IDispatchProps) => {

    const {
        stepName,
        translation,
        validation,
        logBusinessEvent,
        handleNext,
    } = props;

    const { subscriberId } = useParams<IRouteParams>();

    const location = useLocation();

    useEffect(() => {
        switch (stepName) {
            case EWizardChangeLockSteps.StepOperationSelection: {
                logBusinessEvent({
                    eventName: ELogBusinessEventName.B2B_BLOCK_MASTER_ON_STEP_1,
                    type: ELogBusinessEventType.common,
                });
                break;
            }
            case EWizardChangeLockSteps.StepDateSelection: {
                logBusinessEvent({
                    eventName: ELogBusinessEventName.B2B_BLOCK_MASTER_ON_STEP_2,
                    type: ELogBusinessEventType.common,
                });
                break;
            }
            case EWizardChangeLockSteps.StepConfirmation: {
                logBusinessEvent({
                    eventName: ELogBusinessEventName.B2B_BLOCK_MASTER_ON_STEP_3,
                    type: ELogBusinessEventType.common,
                });
                break;
            }
        }
    },[stepName, logBusinessEvent])

    /**
     * Load data to start wizard from SubscriberId page
     */
    const getStartData = useCallback((): IWizardChangeLockStartData => {
        const locationHash = location.hash.replace('#', '');
        return subscriberId && locationHash
            ? { operationType: locationHash as EChangeLockOperationType }
            : {};
    }, [location, subscriberId]);

    const renderStep = useCallback(() => {
        switch (stepName) {
            case EWizardChangeLockSteps.StepOperationSelection:
                return <WizardChangeLockOperationSelection/>;
            case EWizardChangeLockSteps.StepDateSelection:
                return <WizardChangeLockDateSelection/>;
            case EWizardChangeLockSteps.StepConfirmation:
                return <WizardChangeLockConfirmation/>;
        }
    }, [stepName]);

    return (
        <Fragment>
            <PageLocale lazy
                        fileNames={[
                            TranslationFileNamesEnum.wizardChangeLock,
                            TranslationFileNamesEnum.wizard,
                        ]}/>

            <Title pageName={ECommonTranslationPages.wizardChangeLock}/>

            <Wizard
                basketType={EBasketType.MOBILE}
                entityId={subscriberId ? parseInt(subscriberId) : WIZARD_ALL_ENTITIES}
                getStartData={getStartData}
                isNextDisabled={!validation?.isValid}
                name={EWizardName.CHANGE_LOCK}
                translationProps={{
                    error: translation?.error,
                    steps: translation?.steps,
                    title: translation?.title,
                }}
                validateError={validation?.error}
                onWizardNextHandler={handleNext}
            >
                {renderStep()}
            </Wizard>

        </Fragment>
    );
};

interface IConnectedProps extends Pick<IWizardState, 'stepName'>, Pick<IWizardChangeLockReducer, 'validation'> {
    translation: IWizardChangeLockTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    stepName: makeSelectWizardStepName(),
    translation: makeSelectTranslationWizardChangeLock(),
    validation: makeSelectWizardChangeLockValidation(),
});

export interface IDispatchProps {
    handleNext: () => void;
    logBusinessEvent: (payload: ILogBusinessEventPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    handleNext: () => dispatch(wizardChangeLockHandleNextAction()),
    logBusinessEvent: (payload) => dispatch(logBusinessEvent(payload)),
});

export default withAsyncStateManagement(
    connect(mapStateToProps, mapDispatchToProps)(WizardChangeLock),
    { reducer: { wizardChangeLock: wizardChangeLockReducer } },
);
